/**
 * A segmented control that is a REAL radio group (spec 120 §4).
 *
 * Himaad's mock draws `[ Once | Repeating ]` and `[ Daily | Weekly | Monthly ]`
 * as welded segments. Two styled `<button>`s would look identical and be wrong
 * three ways: no single tab stop, no arrow-key traversal, and no selected state
 * exposed to assistive tech. Native same-name radios give all three for free —
 * the browser's own roving focus is the arrow-key implementation, and
 * `checked` is the state. So the radios are real and kept in the accessibility
 * tree; only their PAINT is replaced.
 *
 * The input is `opacity-0` and stretched over its whole segment rather than
 * `sr-only`. Two reasons, both learned the hard way here:
 *   - it stays the topmost hit target, so a Playwright `.check()` lands on the
 *     radio itself instead of being intercepted by a label overlay. One e2e
 *     locator drives this control (`e2e/queue-round2-screenshots.spec.js:66`)
 *     and it does so as `.check().catch(() => {})`, i.e. it fails SOFT — a
 *     control it cannot click yields screenshots of the wrong state while the
 *     spec still passes, so it will not tell you if this breaks.
 *   - `sr-only`'s 1px clip box makes the same click ambiguous.
 *
 * ## `ariaLabel` per option is load-bearing, not decoration
 *
 * The visible segment says `Repeating`; the accessible name stays
 * `On a repeating schedule`. That is deliberate: it is the more descriptive
 * name for AT, AND it keeps four locators in files this stream does not own
 * working across a purely visual change — `ReportQueue.picker.test.jsx:114-115`
 * and `ReportSchedules.create.test.jsx:194-195`, each asserting one of the two
 * long names is CHECKED and the other is not. A visual
 * refresh that silently breaks a peer's locator produces no git conflict and no
 * red suite of its own — only the rebaser ever sees it.
 *
 * ## Measured contrast (SC 1.4.3 text, SC 1.4.11 non-text + state)
 *
 * Every ratio below is recomputed from the hexes in `src/index.css` against the
 * background element that is actually behind that pixel — not quoted from a
 * doc, and not assumed to be page white. The track is `bg-gray-100` #f3f4f6 and
 * the selected segment is white, so most of these have two answers:
 *
 *   selected label   #1d4ed8 on #ffffff              6.70:1   PASS (text)
 *   unselected label #374151 on #f3f4f6              9.37:1   PASS (text)
 *   group border     #6b7280  4.83:1 on #ffffff (outer edge)
 *                             4.39:1 on #f3f4f6 (inner edge)  PASS both
 *   selected ring    #6b7280  4.83:1 on #ffffff (the segment)
 *                             4.39:1 on #f3f4f6 (the track)   PASS both
 *   focus outline    #09203d  16.3:1 on #ffffff / 14.8:1 on #f3f4f6  PASS
 *
 * Both the group border and the selected ring use `--color-rc-seam`, not
 * `--color-rc-control`: each has a tinted surface on ONE OR BOTH sides, and
 * `--color-rc-control` #8b95a5 is only 3.03:1 against page white — 2.75:1
 * against this track, i.e. under the SC 1.4.11 floor on the side that matters
 * for a segment boundary. That exact substitution shipped at 2.780:1 once
 * already (spec 092 E4), which is why the seam token exists.
 *
 * State is never carried by colour alone — `checked` on a real radio is the
 * primary signal, the ring and the fill are the secondary one.
 */
const SegmentedRadio = ({ name, options = [], value, onChange, ariaLabel, labelledBy, className = '' }) => (
  <div
    role="radiogroup"
    aria-label={labelledBy ? undefined : ariaLabel}
    aria-labelledby={labelledBy}
    className={`inline-flex w-full gap-0.5 rounded-rc-control border border-rc-seam bg-gray-100 p-0.5 ${className}`}
  >
    {options.map((o) => {
      const on = value === o.key
      return (
        <label key={o.key} className="relative flex-1">
          <input
            type="radio"
            name={name}
            value={o.key}
            checked={on}
            aria-label={o.ariaLabel || o.label}
            onChange={() => onChange?.(o.key)}
            className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
          <span
            className={`block rounded-rc-control px-2 py-1.5 text-center text-[12.5px] font-semibold peer-focus-visible:outline-2 peer-focus-visible:outline-offset-1 peer-focus-visible:outline-cb-navy ${
              on ? 'bg-white text-rc-chip-text ring-1 ring-inset ring-rc-seam' : 'text-rc-ink-2'
            }`}
          >
            {o.label}
          </span>
        </label>
      )
    })}
  </div>
)

export default SegmentedRadio
